import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Input, Select, DatePicker, Spin, Upload, Button } from "antd";
import {
  fetchCertificates,
  addCertificate,
  handleNextStep,
  handlePrevStep,
} from "../../../features/profile/profileSlice";
import { showToastError, showToastSuccess } from "../../../utils";
import toast from "react-hot-toast";
import docImg from "../../../assets/docImg.png";
import dayjs from "dayjs";
import profileApi from "../../../api/profileApi";

function closeModal() {
  toast.dismiss();
}

const categoryOptions = [
  { label: "Academic", value: "ACADEMIC" },
  { label: "Professional", value: "PROFESSIONAL" },
];

export default function DocumentStep() {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const formRef = useRef(null);

  const { profileLoading, certificates = [] } = useSelector(
    (state) => state.profile
  );

  const [showForm, setShowForm] = useState(false);
  const [uploadedFilePath, setUploadedFilePath] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    dispatch(fetchCertificates());
  }, [dispatch]);

  async function handleFileUpload(file) {
    setUploading(true);
    try {
      const res = await profileApi.uploadFile(file);
      setUploadedFilePath(res.data.path);
      showToastSuccess("File uploaded", closeModal);
    } catch {
      showToastError("File upload failed", closeModal);
    } finally {
      setUploading(false);
    }
    return false; // prevent antd auto-upload
  }

  const onFinish = async (values) => {
    const payload = {
      category: values.category,
      certificateName: values.certificateName,
      certificateNumber: values.certificateNumber || "",
      profession: values.profession || "",
      institution: values.institution,
      endDate: values.endDate
        ? dayjs(values.endDate).format("YYYY-MM-DD")
        : null,
      certificatePath: uploadedFilePath || "",
    };

    const res = await dispatch(addCertificate(payload));
    if (res?.payload?.success) {
      showToastSuccess("Certificate added successfully", closeModal);
      form.resetFields();
      setUploadedFilePath(null);
      setShowForm(false);
      dispatch(fetchCertificates());
    } else {
      showToastError(
        res?.payload?.message || "Failed to add certificate",
        closeModal
      );
    }
  };

  function handleGoNext() {
    dispatch(handleNextStep());
  }

  function handleGoBack() {
    dispatch(handlePrevStep());
  }

  function handleCancel() {
    form.resetFields();
    setUploadedFilePath(null);
    setShowForm(false);
  }

  if (profileLoading && !certificates.length && !showForm) {
    return (
      <div className="flex items-center justify-center mt-20">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="mt-10">
      <h3 className="edit-step-head">Certificates</h3>
      <p className="edit-step-desc mt-2">
        Upload documents to support your application including certificates, CV
        and cover letter
      </p>

      <div className="flex justify-end w-full">
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="bg-transparent flex items-center border-none"
          >
            <div className="bg-darkBlue rounded-full p-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M4 16C4 15.4477 4.44772 15 5 15H27C27.5523 15 28 15.4477 28 16C28 16.5523 27.5523 17 27 17H5C4.44772 17 4 16.5523 4 16Z"
                  fill="white"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M16 4C16.5523 4 17 4.44772 17 5V27C17 27.5523 16.5523 28 16 28C15.4477 28 15 27.5523 15 27V5C15 4.44772 15.4477 4 16 4Z"
                  fill="white"
                />
              </svg>
            </div>
            <span className="text-darkBlue font-medium text-[17px] ml-3">
              Add Certificate
            </span>
          </button>
        )}
      </div>

      {/* Existing certificates list */}
      {certificates.length > 0 && !showForm && (
        <>
          {certificates.map((item) => (
            <div key={item.id} className="bg-white p-6 mb-4 mt-4 rounded-lg">
              <div className="w-full flex flex-col">
                <div className="flex items-center justify-between">
                  <h3 className="text-darkBlue text-[21px] font-bold">
                    {item.certificateName}
                  </h3>
                  <span className="bg-blue-100 text-darkBlue text-[13px] font-medium px-3 py-1 rounded-full">
                    {item.category}
                  </span>
                </div>
                <p className="text-black333 font-medium text-[18px] my-1">
                  {item.institution}
                </p>
                {item.certificateNumber && (
                  <span className="text-black333 font-normal text-[16px]">
                    Certificate No: {item.certificateNumber}
                  </span>
                )}
                {item.profession && (
                  <span className="text-black333 font-normal text-[16px]">
                    Profession: {item.profession}
                  </span>
                )}
                {item.endDate && (
                  <span className="text-black333 font-normal text-[17px] mt-1">
                    {dayjs(item.endDate).format("MMM YYYY")}
                  </span>
                )}
              </div>
            </div>
          ))}

          <div className="flex flex-col sm:flex-row items-center justify-center mt-10 mb-14 w-full">
            <button
              onClick={handleGoBack}
              className="bg-white rounded-[35px] text-darkBlue border-2 border-darkBlue w-full sm:w-[300px] p-2 mb-2 sm:mb-0 sm:mr-3"
              type="button"
            >
              Back
            </button>
            <button
              onClick={handleGoNext}
              className="bg-darkBlue rounded-[35px] text-white w-full sm:w-[300px] p-3"
              type="button"
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Empty state */}
      {!certificates.length && !showForm && (
        <>
          <div className="flex items-center justify-center flex-col mt-10">
            <img
              className="object-contain w-full max-w-[550px] h-auto"
              src={docImg}
              alt="certificates"
            />
            <p className="edit-step-desc my-10">
              Don't have a certificate? You can skip this part
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center mt-10 mb-14 w-full">
            <button
              onClick={handleGoBack}
              className="bg-white rounded-[35px] text-darkBlue border-2 border-darkBlue w-full sm:w-[300px] p-2 mb-2 sm:mb-0 sm:mr-3"
              type="button"
            >
              Back
            </button>
            <button
              onClick={handleGoNext}
              className="bg-darkBlue rounded-[35px] text-white w-full sm:w-[300px] p-3"
              type="button"
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Add certificate form */}
      {showForm && (
        <Form
          layout="vertical"
          ref={formRef}
          name="add-certificate"
          onFinish={onFinish}
          style={{ maxWidth: "100%", marginTop: 20 }}
          form={form}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <Form.Item
              name="category"
              label="Category"
              rules={[{ required: true, message: "Category is required" }]}
            >
              <Select
                style={{ width: "100%", height: "44px" }}
                options={categoryOptions}
                placeholder="Select category"
              />
            </Form.Item>

            <Form.Item
              name="certificateName"
              label="Certificate Name"
              rules={[
                { required: true, message: "Certificate name is required" },
              ]}
            >
              <Input
                className="rounded-[8px] h-[44px] w-full border border-black"
                placeholder="e.g. Degree"
              />
            </Form.Item>

            <Form.Item
              name="certificateNumber"
              label="Certificate Number"
            >
              <Input
                className="rounded-[8px] h-[44px] w-full border border-black"
                placeholder="e.g. 122332"
              />
            </Form.Item>

            <Form.Item
              name="profession"
              label="Profession"
            >
              <Input
                className="rounded-[8px] h-[44px] w-full border border-black"
                placeholder="e.g. Software Engineering"
              />
            </Form.Item>

            <Form.Item
              name="institution"
              label="Institution"
              rules={[
                { required: true, message: "Institution is required" },
              ]}
            >
              <Input
                className="rounded-[8px] h-[44px] w-full border border-black"
                placeholder="e.g. TUK"
              />
            </Form.Item>

            <Form.Item
              name="endDate"
              label="End Date"
            >
              <DatePicker
                style={{ width: "100%", height: "44px" }}
                format="YYYY-MM-DD"
                placeholder="Select end date"
                picker="month"
              />
            </Form.Item>

            <Form.Item label="Upload Certificate" className="col-span-2">
              <Upload
                accept="image/*,.pdf"
                showUploadList={false}
                beforeUpload={handleFileUpload}
                maxCount={1}
              >
                <Button loading={uploading}>
                  {uploading ? "Uploading..." : "Choose File"}
                </Button>
              </Upload>
              {uploadedFilePath && (
                <div className="mt-2 flex items-center gap-3">
                  {uploadedFilePath.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                    <img
                      src={`${process.env.REACT_APP_API_BASE_URL}${uploadedFilePath}`}
                      alt="certificate preview"
                      className="w-24 h-24 object-cover rounded-lg border"
                    />
                  ) : (
                    <a
                      href={`${process.env.REACT_APP_API_BASE_URL}${uploadedFilePath}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-darkBlue underline text-sm"
                    >
                      View uploaded file
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => setUploadedFilePath(null)}
                    className="text-red-500 text-sm"
                  >
                    Remove
                  </button>
                </div>
              )}
            </Form.Item>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-end mt-10 mb-14 w-full">
            <button
              onClick={handleCancel}
              className="bg-white rounded-[35px] text-darkBlue border-2 border-darkBlue w-full sm:w-[300px] p-2 mb-2 sm:mb-0 sm:mr-3"
              type="button"
            >
              Cancel
            </button>
            <button
              className="bg-darkBlue rounded-[35px] text-white w-full sm:w-[300px] p-3"
              type="submit"
              disabled={profileLoading}
            >
              {profileLoading ? <Spin size="small" /> : "Save"}
            </button>
          </div>
        </Form>
      )}
    </div>
  );
}
